/**
 * Summary View for CSV Import Review
 *
 * Displays inline accordion sections for each record category (create, update,
 * invalid). Sections with 0 records are hidden. Each section expands to show
 * the individual rows with their property data.
 *
 * Association tables (PendingAssociationsTable, UnmatchedAssociationsTable)
 * live in ./summary/AssociationTables.tsx.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Link01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { useEntityTypeLabel } from '@/lib/crm/hooks/useEntityTypeLabel';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import type { CsvImport } from '@/lib/csv-import/api';
import { getFileResults } from '@/lib/csv-import/api';
import { apiPost } from '@/lib/http/client';
import type { CrmObject } from '@/types/crm';

import { useEntitySchemaMap } from '../hooks/useEntitySchemaMap';
import { DuplicateImportWarning } from './DuplicateImportWarning';
import { LazyPanelContent } from './LazyPanelContent';
import {
  PendingAssociationsTable,
  UnmatchedAssociationsTable,
} from './summary/AssociationTables';
import { CategorySection } from './summary/CategorySection';
import { InvalidAssociationsBanner } from './summary/InvalidAssociationsTable';
import { SummaryFooter } from './summary/SummaryFooter';
import type { RowCategory, RowData } from './summary/types';
import {
  CATEGORIES,
  computeImportTotals,
  getMatchColumnsRequiredFilenames,
  getPropertyLabel,
  getReimportBlockEnabled,
  mapAnalyzedRowToRowData,
  SCHEMA_STALE_TIME,
} from './summary/types';
import { buildFileBreakdown } from './summaryHelpers';

// Re-export for consumers (ReviewModal, ReviewStep)
export { SummaryFooter } from './summary/SummaryFooter';

// ============================================================================
// Types
// ============================================================================

interface SummaryViewProps {
  importData: CsvImport;
  orgId: number;
  onApprove: () => void;
  onResolveConflicts: () => void;
  onEditMappings?: () => void;
  isApproving: boolean;
  /** When true, the footer (stats + buttons) is NOT rendered inside SummaryView.
   *  The parent should render <SummaryFooter> in its own fixed footer area. */
  externalFooter?: boolean;
  /** Kick off / resume the agent-coercion review (shown in the Invalid section). */
  onReviewCoercions?: () => void;
  /** Label for the coercion CTA. */
  coercionCtaLabel?: string;
  /** Whether the coercion suggestion request is in flight. */
  isReviewingCoercions?: boolean;
}

interface BatchObjectsResponse {
  objects: Record<string, CrmObject>;
}

// ============================================================================
// Main Component
// ============================================================================

export function SummaryView({
  importData,
  orgId,
  onApprove,
  onResolveConflicts,
  onEditMappings,
  isApproving,
  externalFooter = false,
  onReviewCoercions,
  coercionCtaLabel,
  isReviewingCoercions,
}: SummaryViewProps) {
  const { getEntityTypeLabel, entityTypes } = useEntityTypeLabel(orgId);

  // Shared fileId → entity_type_name lookup — used by associationSummary
  // and passed to child components that also need it.
  const fileToEntityType = useMemo(() => {
    const map: Record<string, string> = {};
    for (const file of importData.uploaded_files) {
      map[String(file.file_id)] = file.entity_type_name;
    }
    return map;
  }, [importData.uploaded_files]);

  // ------------------------------------------------------------------
  // Totals from file summaries
  // ------------------------------------------------------------------
  const totals = useMemo(
    () => computeImportTotals(importData.analysis_result),
    [importData.analysis_result]
  );

  const matchColumnsRequiredFilenames = useMemo(
    () => getMatchColumnsRequiredFilenames(importData.analysis_result),
    [importData.analysis_result]
  );

  const reimportBlockEnabled = useMemo(
    () => getReimportBlockEnabled(importData.analysis_result),
    [importData.analysis_result]
  );

  // ------------------------------------------------------------------
  // Extract rows per category (raw — error messages use internal
  // property names until schemas load, then a downstream memo
  // enriches them with human-readable labels).
  // ------------------------------------------------------------------
  const rawRowsByCategory = useMemo(() => {
    const result: Record<RowCategory, RowData[]> = {
      create: [],
      update: [],
      batch_dedup: [],
      invalid: [],
      ambiguous: [],
    };
    const analysisResult = importData.analysis_result;
    if (!analysisResult) {
      return result;
    }
    const fileResults = getFileResults(analysisResult);
    for (const [fileId, fileData] of Object.entries(fileResults)) {
      for (const row of fileData.analyzed_rows ?? []) {
        const cat: RowCategory = row.batch_deduplicated
          ? 'batch_dedup'
          : (row.action as RowCategory);
        if (!result[cat]) {
          continue;
        }
        result[cat].push(
          mapAnalyzedRowToRowData(row, `${fileId}-${row.row_index}`, fileId)
        );
      }
    }
    return result;
  }, [importData.analysis_result]);

  // ------------------------------------------------------------------
  // Per-category conflict counts. The conflict-resolution UI is global
  // (one flow surfaces every conflict across all categories), but the
  // accordion badge needs to know how many conflicts belong to which
  // section so the user can navigate to them. We derive this from
  // rowsByCategory.hasConflict rather than a flat total so update-vs-
  // batch_dedup conflicts surface under the correct heading.
  // ------------------------------------------------------------------
  const categoryConflictCounts = useMemo(() => {
    const counts: Partial<Record<RowCategory, number>> = {};
    for (const cat of CATEGORIES) {
      const n = rawRowsByCategory[cat].filter(r => r.hasConflict).length;
      if (n > 0) {
        counts[cat] = n;
      }
    }
    return counts;
  }, [rawRowsByCategory]);

  // ------------------------------------------------------------------
  // Which properties conflict, per row. Lets the tables mark the offending
  // cell and name the field, rather than flagging the row and making the
  // user open the resolver to find out what it meant.
  //
  // Read straight from the analyzer's conflict list — never recomputed by
  // comparing CSV values against CRM values here. The two are stored in
  // different shapes (raw vs normalized), so a client-side comparison would
  // reproduce issue #9928 in the UI.
  // ------------------------------------------------------------------
  const conflictsByRow = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const conflict of importData.analysis_result?.conflicts ?? []) {
      const rowId = `${conflict.file_id}-${conflict.row_index}`;
      (map[rowId] ??= new Set()).add(conflict.property_name);
    }
    return map;
  }, [importData.analysis_result]);

  // ------------------------------------------------------------------
  // Reverse index: rowId → category. Used to look up which accordion to
  // expand when a user clicks a pending-association cell.
  // ------------------------------------------------------------------
  const rowIdToCategory = useMemo(() => {
    const map = new Map<string, RowCategory>();
    for (const cat of CATEGORIES) {
      for (const row of rawRowsByCategory[cat]) {
        map.set(row.id, cat);
      }
    }
    return map;
  }, [rawRowsByCategory]);

  // ------------------------------------------------------------------
  // Accordion expansion state — lifted here so association-row clicks can
  // open the right category programmatically. The Set holds the set of
  // currently-expanded categories; categories not in the set are collapsed.
  // ------------------------------------------------------------------
  const [expandedCategories, setExpandedCategories] = useState<
    Set<RowCategory>
  >(() => new Set());
  const handleCategoryExpandedChange = useCallback(
    (category: RowCategory, isOpen: boolean) => {
      setExpandedCategories(prev => {
        const next = new Set(prev);
        if (isOpen) {
          next.add(category);
        } else {
          next.delete(category);
        }
        return next;
      });
    },
    []
  );

  // Row id to scroll to + highlight inside whichever table contains it.
  // Cleared a few seconds after being set so the highlight is transient.
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (focusedRowId === null) {
      return undefined;
    }
    const t = setTimeout(() => setFocusedRowId(null), 2500);
    return () => clearTimeout(t);
  }, [focusedRowId]);

  const handleNavigateToRow = useCallback(
    (rowId: string) => {
      const category = rowIdToCategory.get(rowId);
      if (!category) {
        return;
      }
      // Make sure the right accordion is expanded so the row is in the DOM,
      // then signal the table to scroll + highlight.
      setExpandedCategories(prev => {
        if (prev.has(category)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(category);
        return next;
      });
      setFocusedRowId(rowId);
    },
    [rowIdToCategory]
  );

  // ------------------------------------------------------------------
  // Property schemas (shared across all categories) — single source of
  // truth via useEntitySchemaMap, reused by LargeImportSummary/CoercionReview.
  // ------------------------------------------------------------------
  const { schemaMap, schemaMapByEntityType } = useEntitySchemaMap(
    orgId,
    importData.uploaded_files
  );

  const newPropertyLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const file of importData.uploaded_files) {
      for (const np of file.new_properties ?? []) {
        if (np.property_name && np.label) {
          labels[np.property_name] = np.label;
        }
      }
    }
    return labels;
  }, [importData.uploaded_files]);

  // ------------------------------------------------------------------
  // Enrich raw rows with human-readable error labels once schemas load.
  // rawRowsByCategory uses internal property names (e.g. ahoy_linkedin_url);
  // this memo replaces them with labels (e.g. LinkedIn URL).
  // ------------------------------------------------------------------
  const rowsByCategory = useMemo(() => {
    const result: Record<RowCategory, RowData[]> = {
      create: [],
      update: [],
      batch_dedup: [],
      invalid: [],
      ambiguous: [],
    };
    for (const cat of CATEGORIES) {
      result[cat] = rawRowsByCategory[cat].map(row => {
        if (!row.rawErrors || row.rawErrors.length === 0) {
          return row;
        }
        const enrichedError = row.rawErrors
          .map(e => {
            const label = getPropertyLabel(
              e.property_name,
              schemaMap,
              newPropertyLabels
            );
            return `${label}: ${e.error}`;
          })
          .join('; ');
        return { ...row, error: enrichedError };
      });
    }
    return result;
  }, [rawRowsByCategory, schemaMap, newPropertyLabels]);

  // ------------------------------------------------------------------
  // Existing CRM objects for update rows
  // ------------------------------------------------------------------
  const existingEntityIds = useMemo(() => {
    return rowsByCategory.update
      .map(r => r.existing_entity_id)
      .filter((id): id is string => !!id);
  }, [rowsByCategory.update]);

  const { data: existingObjectsMap } = useQuery({
    queryKey: ['existingCrmObjects', orgId, existingEntityIds],
    queryFn: async () => {
      if (existingEntityIds.length === 0) {
        return {};
      }
      const response = await apiPost<BatchObjectsResponse>(
        `/api/orgs/${orgId}/crm-events/crm-objects/by-entity-ids/`,
        // by-entity-ids now defaults include_associations to FALSE. Nothing here
        // reads associations — the association pairs this view renders come from
        // `row.associationDisplay` (the import analysis), not from these
        // objects — so this passes false explicitly to guard against a future
        // default flip.
        { entity_ids: existingEntityIds, include_associations: false }
      );
      return response.objects;
    },
    enabled: existingEntityIds.length > 0,
    staleTime: SCHEMA_STALE_TIME,
  });

  // ------------------------------------------------------------------
  // Associations summary
  // ------------------------------------------------------------------
  const pendingAssociations = useMemo(() => {
    return importData.analysis_result?.associations?.pending_associations ?? [];
  }, [importData.analysis_result]);

  const unmatchedAssociations = useMemo(() => {
    return (
      importData.analysis_result?.associations?.unmatched_associations ?? []
    );
  }, [importData.analysis_result]);

  const invalidAssociations = useMemo(() => {
    return importData.analysis_result?.associations?.invalid_associations ?? [];
  }, [importData.analysis_result]);

  const associationSummary = useMemo(() => {
    if (pendingAssociations.length === 0) {
      return [];
    }
    const pairCounts: Record<string, number> = {};
    for (const assoc of pendingAssociations) {
      const sourceType = fileToEntityType[String(assoc.source_file_id)] ?? '';
      const targetType = fileToEntityType[String(assoc.target_file_id)] ?? '';
      if (sourceType && targetType) {
        const key = `${sourceType}|${targetType}`;
        pairCounts[key] = (pairCounts[key] ?? 0) + 1;
      }
    }
    return Object.entries(pairCounts).map(([key, count]) => {
      const [source, target] = key.split('|');
      return { source, target, count };
    });
  }, [pendingAssociations, fileToEntityType]);

  // ------------------------------------------------------------------
  // File breakdown for the Files section — one row per uploaded file with
  // a per-entity-type record count.
  // ------------------------------------------------------------------
  const fileBreakdown = useMemo(
    () =>
      buildFileBreakdown(
        importData.uploaded_files,
        importData.analysis_result?.file_summaries
      ),
    [importData.uploaded_files, importData.analysis_result]
  );

  return (
    // pb-6 gives the Files section (last child) breathing room above the
    // modal footer / scroll-area edge. Without it, the section sits flush
    // against the bottom of the modal body.
    <div className='space-y-3 pb-6'>
      <DuplicateImportWarning analysisResult={importData.analysis_result} />

      {/* Invalid Association Mappings — always expanded, not an accordion.
          Unlike the pending/unmatched sections below, this is a config
          problem the user should notice immediately, not something to
          discover by expanding a section. */}
      <InvalidAssociationsBanner
        invalidAssociations={invalidAssociations}
        importData={importData}
      />

      {/* Category Accordion Sections */}
      {CATEGORIES.map(cat => (
        <CategorySection
          key={cat}
          category={cat}
          rows={rowsByCategory[cat]}
          importData={importData}
          schemaMap={schemaMap}
          schemaMapByEntityType={schemaMapByEntityType}
          newPropertyLabels={newPropertyLabels}
          existingObjectsMap={existingObjectsMap}
          orgId={orgId}
          conflictsByRow={conflictsByRow}
          conflictCount={categoryConflictCounts[cat]}
          getEntityTypeLabel={getEntityTypeLabel}
          isExpanded={expandedCategories.has(cat)}
          onExpandedChange={handleCategoryExpandedChange}
          focusedRowId={focusedRowId}
          onReviewCoercions={cat === 'invalid' ? onReviewCoercions : undefined}
          coercionCtaLabel={coercionCtaLabel}
          isReviewingCoercions={isReviewingCoercions}
        />
      ))}

      {/* Pending Associations — expandable accordion with table of records */}
      {associationSummary.length > 0 && (
        <AccordionItem
          aria-label={`${pendingAssociations.length} associations detected`}
          className='bg-utility-success-50 border border-utility-success-200 rounded-lg overflow-hidden'
        >
          <AccordionTrigger className='w-full flex items-start gap-2 px-3 py-3 text-left hover:bg-utility-success-100/50 transition-colors'>
            <AccordionChevron
              variant='leading'
              className='w-4 h-4 text-utility-success-600 mt-0.5'
            />
            <Link01
              aria-hidden={true}
              className='w-4 h-4 text-utility-success-600 flex-shrink-0 mt-0.5'
            />
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium text-utility-success-800'>
                {pendingAssociations.length} Association
                {pendingAssociations.length === 1 ? '' : 's'} Detected
              </p>
              <div className='mt-1 space-y-0.5'>
                {associationSummary.map(({ source, target, count }) => (
                  <p
                    key={`${source}-${target}`}
                    className='text-xs text-utility-success-700'
                  >
                    {count} {getEntityTypeLabel(source)} →{' '}
                    {getEntityTypeLabel(target)}
                  </p>
                ))}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionPanel className='border-t border-utility-success-200 bg-bg-primary p-3'>
            <LazyPanelContent>
              <PendingAssociationsTable
                pendingAssociations={pendingAssociations}
                importData={importData}
                getEntityTypeLabel={getEntityTypeLabel}
                onNavigate={handleNavigateToRow}
              />
            </LazyPanelContent>
          </AccordionPanel>
        </AccordionItem>
      )}

      {/* Unmatched Associations — expandable accordion with table of records */}
      {unmatchedAssociations.length > 0 && (
        <AccordionItem
          aria-label={`Unmatched associations, ${unmatchedAssociations.length} entries`}
          className='bg-utility-warning-50 border border-utility-warning-200 rounded-lg overflow-hidden'
        >
          <AccordionTrigger className='w-full flex items-start gap-2 px-3 py-3 text-left hover:bg-utility-warning-100/50 transition-colors'>
            <AccordionChevron
              variant='leading'
              className='w-4 h-4 text-utility-warning-600 mt-0.5'
            />
            <Link01
              aria-hidden={true}
              className='w-4 h-4 text-utility-warning-600 flex-shrink-0 mt-0.5'
            />
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium text-utility-warning-800'>
                Unmatched associations
              </p>
              <p className='text-xs text-utility-warning-700 mt-1'>
                {unmatchedAssociations.length} value
                {unmatchedAssociations.length === 1 ? ' has' : 's have'} no
                matching record. Those links will be skipped.
              </p>
            </div>
          </AccordionTrigger>
          <AccordionPanel className='border-t border-utility-warning-200 bg-bg-primary p-3'>
            <LazyPanelContent>
              <UnmatchedAssociationsTable
                unmatchedAssociations={unmatchedAssociations}
                importData={importData}
              />
            </LazyPanelContent>
          </AccordionPanel>
        </AccordionItem>
      )}

      {/* Conflict Warning */}
      {totals.conflicts > 0 && (
        <div className='p-3 bg-utility-warning-50 border border-utility-warning-200 rounded-lg'>
          <div className='flex items-start gap-2'>
            <AlertTriangle
              aria-hidden={true}
              className='w-4 h-4 text-utility-warning-600 flex-shrink-0 mt-0.5'
            />
            <div className='flex-1'>
              <p className='text-sm font-medium text-utility-warning-800'>
                {totals.conflicts} Conflict
                {totals.conflicts === 1 ? '' : 's'} Detected
              </p>
              <p className='text-xs text-utility-warning-700 mt-1'>
                Your CSV and existing CRM data overlap in some fields. You can
                resolve conflicts one by one, or choose a bulk rule &mdash; like
                keeping the most complete value for each field.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* File Breakdown — one row per uploaded file, with the record count for
          each entity type it produces. */}
      {fileBreakdown.length > 0 && (
        <div className='border border-[var(--color-border-secondary)] rounded-lg p-3'>
          <h4 className='text-xs font-medium text-[var(--color-text-primary)] mb-2'>
            Files
          </h4>
          <div className='space-y-2'>
            {fileBreakdown.map(group => (
              <div key={group.key}>
                <div className='text-xs font-medium text-[var(--color-text-secondary)]'>
                  {group.sourceFilename}
                </div>
                {/* Counts use SECONDARY (informational), not tertiary (which the design
                    system reserves for disabled/placeholder text). */}
                <div className='ml-3 mt-0.5 text-xs text-[var(--color-text-secondary)]'>
                  {group.types
                    .map(
                      t =>
                        `${t.recordCount} ${getTypeLabelByName(
                          t.entityTypeName,
                          { plural: t.recordCount !== 1 },
                          entityTypes
                        )}`
                    )
                    .join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inline footer (when not using external footer) */}
      {!externalFooter && (
        <SummaryFooter
          totals={totals}
          onApprove={onApprove}
          onResolveConflicts={onResolveConflicts}
          onEditMappings={onEditMappings}
          isApproving={isApproving}
          matchColumnsRequiredFilenames={matchColumnsRequiredFilenames}
          reimportBlockEnabled={reimportBlockEnabled}
          invalidAssociationsCount={invalidAssociations.length}
        />
      )}
    </div>
  );
}
